/**
 * "Delete this submission" — **ONE component, two mount points**: the tracker's
 * detail dialog on `/event-interest`, and the company record's own
 * `CompanyEventInterestPanel`.
 *
 * ⚠️ **Everything hard here is state PER RECORD rather than layout** — the
 * in-flight guard, the refusal wording, and the three different things a `404`
 * has to do. Two copies would agree the day they were written and drift on the
 * first correction, and what would drift is a sentence about destroying a
 * customer's answers. Same shape, and the same reasoning, as
 * `ResendApprovalControl`.
 *
 * ── WHY THE CONFIRMATION NAMES EVERY PROJECT ────────────────────────────────
 *
 * Deleting a submission also takes the company off every project of interest
 * that submission's `yes` answers named, which is what `ConfirmDialog` body text
 * is for under this repo's rules: *name the record and state the real
 * consequence*. Taking a project of interest off creates or closes nothing else
 * (owner's decision 2026-10-12: projects of interest are a list on the company,
 * and the sales tracker no longer has pursuits), so the dialog no longer
 * mentions one.
 *
 * ⛔ **NO SENTENCE HERE MAY PROMISE AN UNDO, IN ANY FORM.** Not "you can add it
 * back", not "this can be reversed", not a softened "may". The submission's
 * answers are gone for good. The dialog says what happens and stops.
 *
 * ── THE NO-HELPER-TEXT PROHIBITION ──────────────────────────────────────────
 *
 * ⚠️ **The dialog body is the documented exception rather than a carve-out from
 * it.** That rule forbids prose attached to a CONTROL — a sentence competing
 * with the thing it sits beside. `ConfirmDialog` body text naming a destructive
 * consequence is one of the four kinds the rule keeps. ⛔ **The BUTTON carries
 * no sentence of its own**, on either mount, and must not gain one.
 */

import { useCallback, useState } from 'react';
import { Button, ConfirmDialog } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';
import { canDeleteSubmissions, type RemovedProject } from './delete-submission';

/**
 * What came back, and what the dialog may do about it.
 *
 * ⚠️ **`retryable` and `gone` are SEPARATE, and neither implies the other.** A
 * refusal and a record somebody else has already deleted both mean *pressing
 * again cannot do anything different*, and only one of them means *the thing
 * you wanted gone is gone*.
 */
interface DeleteFailure {
  message: string;
  /** Whether pressing Delete again could plausibly answer differently. */
  retryable: boolean;
  /** Whether the submission is now absent — so the caller's list is stale. */
  gone: boolean;
}

/**
 * ⚠️ **A `403` CARRIES NO `details`, so it is a BANNER and never a field
 * error** — there is no field on this dialog for one to bind to, and a detail
 * routed nowhere still inflates a "one field needs attention" count on the
 * forms that have one.
 *
 * ⚠️ **Our sentence rather than the server's on the `403`**, matching
 * `NotesPanel`: `requireRole` answers a generic refusal that says less than
 * this one does, and this one names the tier. Every other status renders the
 * server's own message verbatim, because on those the server knows more than we
 * do.
 *
 * 🔴 **A `404` IS NOT AN ERROR IN THIS APP AND MUST NOT BE WORDED AS ONE.** The
 * contract answers it both for a submission that is not there and for a company
 * that is not there — and the second implies the first, since a company delete
 * is `ON DELETE RESTRICT` while submissions point at it. Either way the record
 * is gone, which is the outcome the reader asked for, so the sentence reports
 * it as done by somebody else rather than as a failure. ⚠️ **It still does not
 * claim WE deleted it**: a re-runnable operation reporting on a record it may
 * or may not have made this time is the proxy-field mistake this repo records
 * five times over.
 */
function failureOf(caught: unknown): DeleteFailure {
  if (caught instanceof ApiError) {
    if (caught.status === 403) {
      return {
        message: 'Your permission set does not include this. An administrator can change that.',
        retryable: false,
        gone: false,
      };
    }
    if (caught.status === 404) {
      return {
        message: 'Somebody else has already deleted this submission.',
        retryable: false,
        gone: true,
      };
    }
    return { message: caught.message, retryable: true, gone: false };
  }
  return {
    message: 'The submission couldn’t be deleted. Check your connection and try again.',
    retryable: true,
    gone: false,
  };
}

export interface DeleteSubmissionControlProps {
  submissionId: number;
  /** The institution, named in the dialog because it is raised from one row of many. */
  institution: string;
  /** The submission's own timestamp — what tells one of an institution's submissions from another. */
  submittedAt: string;
  /**
   * The projects this submission's `yes` answers named, which are the projects
   * of interest the delete takes the company off.
   *
   * ⚠️ **Empty is a REAL state and gets its own sentence** — an institution that
   * said no to every festival has no interest to remove, and a dialog naming an
   * empty list would read as a rendering fault on the one screen that must be
   * read carefully.
   */
  projects: readonly RemovedProject[];
  /**
   * Called once the submission is gone.
   *
   * ⚠️ **Read what a caller's handler DOES before wiring one in.** Several
   * callbacks in this codebase named like this mean *close the dialog*, and
   * calling one as a notification unmounts the thing that was about to report.
   * Here it fires only after the record is actually absent, so closing is a
   * legitimate thing for a caller to do with it — but the caller must also
   * re-read whatever it is showing, because a deleted submission changes the
   * company's projects of interest and the demand figures as well as the list.
   */
  onDeleted: () => void;
  size?: 'sm' | 'md';
}

export function DeleteSubmissionControl({
  submissionId,
  institution,
  submittedAt,
  projects,
  onDeleted,
  size = 'md',
}: DeleteSubmissionControlProps) {
  const { ability } = useAuth();

  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [failure, setFailure] = useState<DeleteFailure | null>(null);

  /**
   * ⚠️ **THE PREVIOUS REFUSAL IS CLEARED ON THE WAY IN.** `Modal` closes on
   * Escape even while Cancel is held down for an in-flight delete, so a failure
   * can outlive the dialog that reported it — and reopening onto a stale
   * *"restricted to administrators"* would be a refusal for a request nobody
   * has made yet, on a dialog whose confirm button it also holds down.
   */
  const open = useCallback(() => {
    setFailure(null);
    setConfirming(true);
  }, []);

  const close = useCallback(() => {
    /*
     * ⚠️ **A `404` LEAVES THE CALLER'S LIST STALE, so closing has to refresh
     * it.** The dialog deliberately stays open showing the sentence rather than
     * silently succeeding — the reader pressed a button and is owed an answer —
     * and the refresh happens on the way out, when the row disappears in front
     * of them rather than behind a dialog they are still reading.
     */
    const stale = failure?.gone ?? false;
    setConfirming(false);
    setFailure(null);
    if (stale) onDeleted();
  }, [failure, onDeleted]);

  const confirm = useCallback(() => {
    setDeleting(true);
    setFailure(null);
    /*
     * ⛔ **NO `AbortSignal`, DELIBERATELY.** Aborting a `DELETE` cancels the
     * LISTENING and not the deletion, so a control that offered to cancel would
     * be reporting an outcome it cannot know — and this repo already refuses to
     * offer an Escape from a save that is in flight for the same reason.
     *
     * ⚠️ **And so no `.finally`**: both arms below settle `deleting`
     * explicitly, and the success arm settles it BEFORE `onDeleted`, which on
     * the tracker unmounts this control. A `.finally` writing state after that
     * would be a guard that can never usefully fire, which this codebase treats
     * as worse than no guard at all.
     */
    api
      .deleteEventInterestSubmission(submissionId)
      .then(() => {
        setDeleting(false);
        setConfirming(false);
        onDeleted();
      })
      .catch((caught: unknown) => {
        setDeleting(false);
        setFailure(failureOf(caught));
      });
  }, [submissionId, onDeleted]);

  /*
   * ⛔ **WITHHELD, NOT DISABLED.** The house rule prefers withholding, and both
   * mounts make it the right call for their own reason: the tracker is a
   * read-only screen where a permanently disabled red button on every
   * submission is a refusal nobody can act on, and the company panel is one
   * panel among several on a record page that renders completely without it.
   *
   * ⚠️ **A disabled `Button` could not carry its own reason anyway** — `Button`
   * sets `disabled:pointer-events-none`, so it never receives the hover and a
   * `title` on it never renders. A why-this-is-disabled sentence would need a
   * wrapper span, which is a sentence beside a control on a screen that has no
   * room for one.
   *
   * ⚠️ **THE GATE IS HERE RATHER THAN AT EACH CALL SITE**, so a third mount
   * cannot forget it — the ruling `InlineTrackerFields` records for the same
   * shape: suppress where the component decides, not where it is used.
   */
  if (!canDeleteSubmissions(ability)) return null;

  return (
    <>
      <Button variant="danger" size={size} onClick={open}>
        Delete submission
      </Button>

      <ConfirmDialog
        open={confirming}
        onClose={close}
        onConfirm={confirm}
        title="Delete this submission?"
        confirmLabel="Delete submission"
        /* ⚠️ Nothing is left to cancel once the record is gone. */
        cancelLabel={failure?.gone === true ? 'Close' : 'Cancel'}
        loading={deleting}
        {...(failure !== null ? { error: failure.message } : {})}
        /*
         * Held down only beside an `error` that says why — a refusal and a
         * record somebody else has deleted both answer identically however many
         * times they are pressed. Every other failure stays retryable.
         */
        confirmDisabled={failure !== null && !failure.retryable}
        description={
          <>
            <p className="m-0">
              This deletes what {institution} answered on {formatDateTime(submittedAt)}. It can't
              be recovered.
            </p>
            {projects.length === 0 ? (
              <p className="m-0 mt-2">
                They said yes to no festival, so no project of interest is removed.
              </p>
            ) : (
              <>
                <p className="m-0 mt-2">It also takes them off these projects of interest:</p>
                {/*
                  ⚠️ **A LIST RATHER THAN A JOINED SENTENCE.** A submission can
                  answer seven festivals, and seven long project names run
                  together are read as one blur at 320px — where the whole
                  requirement is that each project being removed is NAMED and
                  seen.
                */}
                <ul className="m-0 mt-1 list-disc pl-5">
                  {projects.map((project) => (
                    <li key={project.key} className="[overflow-wrap:anywhere]">
                      {project.name}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        }
      />
    </>
  );
}
